/**
 * MRN label constants.
 *
 * The tenant-configured term for the medical record number (Settings →
 * Hospital Configuration) is a *label*: it changes what the field is called,
 * never the stored value. It reaches the UI as the `{{mrnLabel}}` i18next
 * interpolation variable — see `shared/composables/useMrnLabel.ts`.
 *
 * The constants live in this leaf module (rather than beside the composable)
 * because both the i18n plugin and the composable need them, and the composable
 * imports the i18n plugin — importing them from there would create a cycle.
 *
 * This module previously also held `applyMrnPrefix` / `splitMrnPrefix`, which
 * baked the configured term into the MRN *value* and produced identifiers like
 * "EKB2608444763" that no longer matched the hospital feed. MRN values are now
 * stored exactly as received, so those helpers and the `MrnPrefixInput`
 * component that used them have been removed.
 */

/**
 * Last-resort term, used only before the locale bundle is available.
 *
 * NOTE: must stay a literal — the `common.mrn` key now resolves to
 * `{{mrnLabel}}`, so deriving this from `t()` would be circular.
 */
export const MRN_LABEL_FALLBACK = 'MRN';

/**
 * Bundle key holding each language's natural term for the medical record
 * number, used when a tenant has configured none ("MRN" in en/de, "NDA" in fr).
 *
 * This exists because the term already varied per language before it became
 * configurable: without it, templating the MRN strings would silently regress
 * every non-English tenant to the English "MRN".
 *
 * Deliberately NOT templated with `{{mrnLabel}}` — it is the base term, so
 * resolving it cannot recurse.
 */
export const MRN_LABEL_DEFAULT_KEY = 'common.mrnLabelDefault';

/** Name of the i18next interpolation variable used by every MRN locale string. */
export const MRN_LABEL_VAR = 'mrnLabel';
